/**
 * Truncates content to a specified number of lines and provides "Read More" / "Read Less" toggle.
 * Applies line-clamp CSS on connect, then uses ResizeObserver to detect when content
 * actually overflows and show/hide the toggle button.
 *
 * Values:
 * - readMoreText: Label shown when content is collapsed (default: "Read more")
 * - readLessText: Label shown when content is expanded (default: "Read less")
 * - lines: Number of lines to clamp to (default: 3)
 *
 * Targets:
 * - text: The element containing the content to truncate
 * - button: The toggle button element
 *
 * CSS Classes:
 * - read-more-clamped: Applied to text target to enable line-clamp truncation
 * - read-more-expanded: Applied to text target when content is expanded
 *
 * The text target receives a --read-more-lines CSS variable matching the lines value,
 * which the read-more-clamped class uses for -webkit-line-clamp.
 */
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    readMoreText: String,
    readLessText: String,
    lines: { type: Number, default: 3 },
  };

  static targets = ["text", "button"];

  initialize() {
    this.isExpanded = false;
  }

  connect() {
    if (!this.hasButtonTarget || !this.hasTextTarget) return;

    this.textTarget.dataset.lines = this.linesValue;
    this.textTarget.classList.add("read-more-clamped");

    this.buttonTarget.addEventListener("click", this.toggle.bind(this));

    this.observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const hasMedia = entry.target.querySelector("video, audio") !== null;

        if (hasMedia) {
          entry.target.classList.remove("read-more-clamped");
          this.buttonTarget.classList.add("d-none");
        } else if (entry.target.innerText.length && this.#isOverflowing(entry.target)) {
          this.buttonTarget.classList.remove("d-none");
        } else if (!this.isExpanded) {
          this.buttonTarget.classList.add("d-none");
        }
      });
    });

    this.observer.observe(this.textTarget);
  }

  disconnect() {
    this.observer.disconnect();
  }

  toggle(event) {
    event.preventDefault();

    if (this.isExpanded) {
      this.textTarget.classList.remove("read-more-expanded");
      this.textTarget.classList.add("read-more-clamped");
      this.buttonTarget.innerText = this.readMoreTextValue;
    } else {
      this.textTarget.classList.add("read-more-expanded");
      this.textTarget.classList.remove("read-more-clamped");
      this.buttonTarget.innerText = this.readLessTextValue;
    }
    this.isExpanded = !this.isExpanded;
  }

  /**
   * Hides the button - useful when entering edit mode from another controller
   */
  hide() {
    this.buttonTarget.classList.add("d-none");
  }

  /**
   * Shows the button if content needs truncation - useful when exiting edit mode
   */
  show() {
    if (this.textTarget.innerText.length && this.#isOverflowing(this.textTarget)) {
      this.buttonTarget.classList.remove("d-none");
    }
  }

  /**
   * Compares scrollHeight against the expected height for the allowed number of lines,
   * using computed lineHeight. This avoids false positives from font-metric discrepancies
   * between scrollHeight and clientHeight.
   */
  #isOverflowing(element) {
    const style = getComputedStyle(element);
    const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2;
    return element.scrollHeight > lineHeight * this.linesValue + lineHeight * 0.5;
  }
}
